/**
 * R-131-EPISTEMIC-GROUNDING-A — structured findings with evidence state.
 * R-131-EPISTEMIC-GROUNDING-C — partition + operator-attestation promotion helpers.
 */
import type { Schema } from '@google/genai';
import { Type } from '@google/genai';

export type FindingEvidenceState = 'confirmed' | 'likely_present_unverified' | 'unknown';

export interface FindingClaimEvidence {
  refIds: number[];
  excerpt: string;
}

export interface FindingClaim {
  claim: string;
  evidenceState: FindingEvidenceState;
  evidence?: FindingClaimEvidence;
  openQuestion?: string;
  suggestedFollowUp?: string;
}

/** Legacy plain-string entries may still exist on old reports until re-run. */
export type FindingClaimEntry = FindingClaim | string;

/** R-131-EPISTEMIC-GROUNDING-C — operator disposition for an open question. */
export type OpenQuestionAnswerValue = 'yes' | 'no' | 'details';

/** Maximum persisted operator detail length; longer input is rejected. */
export const OPEN_QUESTION_DETAILS_MAX_CHARS = 2000;

/** Stable identity inputs for an open question within a generated report. */
export interface OpenQuestionIdentity {
  reportId: string;
  sectionId?: string;
  question: string;
  /** Historical alternate proposition text used to migrate pre-canonical keys. */
  questionAliases?: readonly string[];
}

export interface ResolvedOpenQuestionIdentity {
  /** Canonical Yes/No proposition shown to and answered by the operator. */
  question: string;
  /** Non-proposition explanatory copy retained separately from the answer. */
  context?: string;
  /** All historical texts that may have participated in a persisted key. */
  questionAliases: string[];
}

/**
 * Durable operator response to a report open question.
 *
 * The report keeps this response as a post-generation overlay. The original
 * FindingClaim and the report's generation-time SiteContext snapshot remain
 * immutable so the generated artifact stays reproducible.
 */
export interface OpenQuestionResponse {
  id: string;
  questionKey: string;
  question: string;
  context?: string;
  answer: OpenQuestionAnswerValue;
  details?: string;
  reportId: string;
  sectionId?: string;
  answeredAt: string;
  answeredBy?: { uid: string; email?: string };
  attestationClaimId: string;
  /** Durable repair marker: report is authoritative while the SED projection retries. */
  sedSyncStatus?: 'pending' | 'synced';
}

/**
 * Format-neutral export projection for one finding row.
 *
 * The generated finding remains immutable. A persisted operator response is
 * attached as a separate overlay so HTML/PDF, DOCX and PPTX can all show the
 * original proposition alongside (rather than replaced by) the disposition.
 */
export interface FindingClaimExportProjection {
  /** Existing customer-facing finding text for confirmed/pending rows. */
  finding: string;
  /** Original proposition shown to the operator for an unknown finding. */
  openQuestion?: string;
  /** Optional explanatory copy retained separately from the proposition. */
  context?: string;
  /** Persisted post-generation response, when this exact question was answered. */
  response?: OpenQuestionResponse;
}

export interface FindingClaimExportProjectionOptions {
  reportId: string;
  sectionId?: string;
  responses?: readonly OpenQuestionResponse[];
}

/** Persistence outcome returned by the interactive report answer action. */
export interface AnswerOpenQuestionResult {
  response: OpenQuestionResponse;
  siteSaved: boolean;
  reportSaved: boolean;
  sedSaved: boolean;
}

function normalizeOpenQuestionIdentityPart(value: string | undefined): string {
  return (value ?? '').normalize('NFKC').replace(/\s+/g, ' ').trim();
}

const CUSTOM_BRIEF_SECTION_PREFIX = 'customBriefQ_';
const LEGACY_CUSTOM_BRIEF_SECTION_PREFIX = 'customBriefQuestion-';

/** Collapse the recovered short-lived custom-brief spelling onto the registry id. */
export function canonicalOpenQuestionSectionId(sectionId: string | undefined): string | undefined {
  const normalized = normalizeOpenQuestionIdentityPart(sectionId);
  if (!normalized) return undefined;
  if (normalized.startsWith(LEGACY_CUSTOM_BRIEF_SECTION_PREFIX)) {
    const questionId = normalized.slice(LEGACY_CUSTOM_BRIEF_SECTION_PREFIX.length);
    return questionId ? `${CUSTOM_BRIEF_SECTION_PREFIX}${questionId}` : normalized;
  }
  return normalized;
}

function openQuestionSectionAliases(sectionId: string | undefined): string[] {
  const original = normalizeOpenQuestionIdentityPart(sectionId);
  const canonical = canonicalOpenQuestionSectionId(sectionId);
  const aliases = new Set<string>();
  if (canonical) aliases.add(canonical);
  if (original) aliases.add(original);
  if (canonical?.startsWith(CUSTOM_BRIEF_SECTION_PREFIX)) {
    const questionId = canonical.slice(CUSTOM_BRIEF_SECTION_PREFIX.length);
    if (questionId) aliases.add(`${LEGACY_CUSTOM_BRIEF_SECTION_PREFIX}${questionId}`);
  }
  if (aliases.size === 0) aliases.add('report');
  return [...aliases];
}

function looksLikeQuestion(value: string): boolean {
  const normalized = normalizeOpenQuestionIdentityPart(value);
  if (!normalized) return false;
  if (normalized.endsWith('?')) return true;
  return /^(?:has|have|had|is|are|was|were|do|does|did|can|could|will|would|should|may|might|must|whether)\b/i.test(normalized);
}

/**
 * Resolve both FindingClaim shapes present in durable reports:
 *
 * - schema-standard: `claim` is context/finding and `openQuestion` is the
 *   operator proposition;
 * - Redhead-era inverted: `claim` is the interrogative proposition and
 *   `openQuestion` is explanatory survey/status copy.
 */
export function resolveOpenQuestionIdentity(
  entry: Pick<FindingClaim, 'claim' | 'openQuestion'>,
): ResolvedOpenQuestionIdentity {
  const claim = normalizeOpenQuestionIdentityPart(entry.claim);
  const openQuestion = normalizeOpenQuestionIdentityPart(entry.openQuestion);
  if (!claim && !openQuestion) {
    return { question: '', questionAliases: [] };
  }

  const claimIsQuestion = looksLikeQuestion(claim);
  const openQuestionIsQuestion = looksLikeQuestion(openQuestion);
  // Prefer the only interrogative. If both/neither look interrogative, honour
  // the documented schema (`openQuestion`) and fall back to the legacy claim.
  const question = claimIsQuestion && !openQuestionIsQuestion
    ? claim
    : openQuestion || claim;
  const contextCandidate = question === claim ? openQuestion : claim;
  const aliases = new Set<string>();
  for (const candidate of [question, claim, openQuestion]) {
    const normalized = normalizeOpenQuestionIdentityPart(candidate);
    if (normalized) aliases.add(normalized);
  }
  return {
    question,
    ...(contextCandidate && contextCandidate !== question ? { context: contextCandidate } : {}),
    questionAliases: [...aliases],
  };
}

function stableFnv1a(value: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(36).padStart(7, '0');
}

/**
 * Site-scoped subject identity for the durable attestation/SED projection.
 *
 * Report response keys intentionally include reportId for artifact history;
 * this key intentionally does not, so answering the same proposition in a
 * later report replaces the live SiteContext claim instead of contradicting it.
 */
export function buildOpenQuestionSubjectKey(
  identity: Pick<OpenQuestionIdentity, 'question'>,
): string {
  const question = normalizeOpenQuestionIdentityPart(identity.question).toLowerCase();
  return `oqs-${stableFnv1a(question)}${stableFnv1a(`open-question-subject:${question}`)}`;
}

/** Build the deterministic key shared by report, SiteContext and SED projections. */
function buildRawOpenQuestionQuestionKey(identity: OpenQuestionIdentity): string {
  const reportId = normalizeOpenQuestionIdentityPart(identity.reportId);
  const sectionId = normalizeOpenQuestionIdentityPart(identity.sectionId) || 'report';
  const question = normalizeOpenQuestionIdentityPart(identity.question).toLowerCase();
  const canonical = `${reportId}\u001f${sectionId}\u001f${question}`;
  return `oq-${stableFnv1a(canonical)}${stableFnv1a(`open-question:${canonical}`)}`;
}

export function buildOpenQuestionQuestionKey(identity: OpenQuestionIdentity): string {
  return buildRawOpenQuestionQuestionKey({
    ...identity,
    sectionId: canonicalOpenQuestionSectionId(identity.sectionId),
  });
}

/** Canonical + historical keys used for non-destructive response migration. */
export function buildOpenQuestionQuestionKeys(identity: OpenQuestionIdentity): string[] {
  const questions = new Set<string>([
    identity.question,
    ...(identity.questionAliases ?? []),
  ].map(normalizeOpenQuestionIdentityPart).filter(Boolean));
  const keys = new Set<string>();
  for (const sectionId of openQuestionSectionAliases(identity.sectionId)) {
    for (const question of questions) {
      keys.add(buildRawOpenQuestionQuestionKey({ ...identity, sectionId, question }));
    }
  }
  return [...keys];
}

/** Find a persisted response by a prebuilt key or by its report identity. */
export function findOpenQuestionResponse(
  responses: readonly OpenQuestionResponse[] | undefined,
  identityOrKey: OpenQuestionIdentity | string,
): OpenQuestionResponse | undefined {
  const questionKeys = typeof identityOrKey === 'string'
    ? [identityOrKey]
    : buildOpenQuestionQuestionKeys(identityOrKey);
  const keySet = new Set(questionKeys);
  return responses?.find((response) => keySet.has(response.questionKey));
}

/**
 * Pair a generated finding with its durable operator-response overlay.
 *
 * Matching uses the same report/section/question identity as the interactive
 * report control. No generated FindingClaim field is rewritten or promoted.
 */
export function projectFindingClaimForExport(
  entry: FindingClaimEntry,
  options: FindingClaimExportProjectionOptions,
): FindingClaimExportProjection {
  const claim = coerceFindingClaimEntry(entry);
  if (!claim) return { finding: '' };

  const finding = formatFindingClaimDisplay(claim);
  if (claim.evidenceState !== 'unknown') return { finding };

  const identity = resolveOpenQuestionIdentity(claim);
  const question = identity.question;
  const response = question
    ? findOpenQuestionResponse(options.responses, {
        reportId: options.reportId,
        sectionId: options.sectionId,
        question,
        questionAliases: identity.questionAliases,
      })
    : undefined;
  const claimContext = identity.context?.trim();
  const responseContext = response?.context?.trim();
  const contextualCopy = claimContext && claimContext !== question
    ? claimContext
    : responseContext && responseContext !== question
      ? responseContext
      : undefined;

  return {
    finding,
    openQuestion: question || undefined,
    ...(contextualCopy ? { context: contextualCopy } : {}),
    ...(response ? { response } : {}),
  };
}

/** Human-readable, lossless lines shared by native document exporters. */
export function findingClaimExportLines(
  projection: FindingClaimExportProjection,
): string[] {
  if (!projection.openQuestion) return projection.finding ? [projection.finding] : [];

  const questionLabel = projection.response ? 'Original question' : 'Open question';
  const lines = [`${questionLabel}: ${projection.openQuestion}`];
  if (projection.context) lines.push(`Context: ${projection.context}`);
  if (projection.response) {
    const disposition = projection.response.answer === 'yes'
      ? 'Yes'
      : projection.response.answer === 'no'
        ? 'No'
        : 'Details recorded';
    const answeredDate = projection.response.answeredAt.slice(0, 10);
    lines.push(
      `Operator response: ${disposition}${answeredDate ? ` · ${answeredDate}` : ''}`,
    );
    const details = projection.response.details?.trim();
    if (details) lines.push(`Operator details: ${details}`);
  }
  return lines;
}

/** Replace a response with the same stable key, collapsing legacy duplicates. */
export function upsertOpenQuestionResponse(
  responses: readonly OpenQuestionResponse[] | undefined,
  response: OpenQuestionResponse,
  equivalentKeys: readonly string[] = [response.questionKey],
): OpenQuestionResponse[] {
  const keys = new Set([response.questionKey, ...equivalentKeys]);
  const next: OpenQuestionResponse[] = [];
  let replaced = false;
  for (const current of responses ?? []) {
    if (!keys.has(current.questionKey)) {
      next.push(current);
      continue;
    }
    if (!replaced) {
      next.push(response);
      replaced = true;
    }
  }
  if (!replaced) next.push(response);
  return next;
}

/** Partitioned epistemic buckets for reader-safe rendering. */
export interface PartitionedFindingClaims {
  confirmed: FindingClaim[];
  verificationPending: FindingClaim[];
  openQuestions: FindingClaim[];
}

const EVIDENCE_STATES: FindingEvidenceState[] = [
  'confirmed',
  'likely_present_unverified',
  'unknown',
];

/** R-131-EPISTEMIC-GROUNDING-B pattern seeds — used by prompt tests and future detector. */
export const FORBIDDEN_ASSERTED_ABSENCE_PATTERNS: RegExp[] = [
  /(?:has\s+not\s+been|is\s+not\s+yet)\s+\w+/i,
  /\b(?:the\s+)?lack\s+of\s+/i,
  /\bno\s+(?:[A-Z][A-Za-z]*\s+)?(?:exists|is\s+in\s+place|is\s+signed|has\s+been)/i,
  /\bnot\s+yet\s+(?:executed|signed|formed|established|binding|firm)/i,
  /\bstill\s+in\s+discussion\b/i,
  /\byet\s+to\s+be\b/i,
];

export function buildEpistemicPreamble(): string {
  return `---
EPISTEMIC GROUNDING (READ FIRST):

You will be tempted to assert absence — "the PPA has not been signed,"
"the SPE has not been formed," "no firm supply agreement exists."

THIS IS FORBIDDEN unless the user context EXPLICITLY says so with a
citable source. If the context simply does not mention X, you MUST
classify X as \`unknown\` and emit an open question — NOT an asserted
absence.

Three permitted classifications for any factual claim about absence:

1. confirmed — Cite the exact passage where the document asserts the
   absence. Use diction like "Documentation confirms..." and include [Ref N].
   Set evidenceState to "confirmed".
2. likely_present_unverified — Context suggests presence but isn't
   definitive. Use diction like "Documentation suggests X; verification
   pending." Treat as a verification task, NOT a red flag.
3. unknown — Context is silent. Emit an open question (see schema).
   Set evidenceState to "unknown". DO NOT phrase as a red flag.

Forbidden phrasings (in claim text when evidenceState is not "confirmed"):
- "X is not yet [verb]"
- "the lack of X"
- "X has not been [verb]"
- "no X exists / is in place / is signed"

Required phrasings for unknowns:
- "Has X been [verb]?"
- "Confirm whether X is [state]."
- "Documentation does not address X; this requires verification."
---`;
}

export function buildEpistemicFindingDirective(sectionLabel: string): string {
  return (
    `${sectionLabel}: Every entry MUST be a FindingClaim object with claim, evidenceState ` +
    `("confirmed" | "likely_present_unverified" | "unknown"), evidence (required when not unknown), ` +
    `and openQuestion (required when unknown). Apply EPISTEMIC GROUNDING — never assert absence without citation.`
  );
}

export const FINDING_CLAIM_EVIDENCE_SCHEMA: Schema = {
  type: Type.OBJECT,
  properties: {
    refIds: {
      type: Type.ARRAY,
      items: { type: Type.INTEGER },
      description: '1-based [Ref N] indices backing this claim.',
    },
    excerpt: {
      type: Type.STRING,
      description: 'Verbatim supporting passage from the cited source.',
    },
  },
  required: ['refIds', 'excerpt'],
};

/** Gemini-compatible FindingClaim item schema (R-131-EPISTEMIC-GROUNDING-A). */
export const FINDING_CLAIM_SCHEMA: Schema = {
  type: Type.OBJECT,
  properties: {
    claim: {
      type: Type.STRING,
      description:
        'The finding sentence. For unknown evidenceState use open-question diction — never assert absence without confirmed + evidence.',
    },
    evidenceState: {
      type: Type.STRING,
      enum: ['confirmed', 'likely_present_unverified', 'unknown'],
      description:
        'confirmed = documented absence/fact with citation; likely_present_unverified = verification task; unknown = silent context → open question.',
    },
    evidence: {
      ...FINDING_CLAIM_EVIDENCE_SCHEMA,
      description: 'Required when evidenceState is confirmed or likely_present_unverified.',
    },
    openQuestion: {
      type: Type.STRING,
      description: 'Required when evidenceState is unknown — verification question, not a red-flag assertion.',
    },
    suggestedFollowUp: {
      type: Type.STRING,
      description: 'Optional operator follow-up when evidenceState is unknown or likely_present_unverified.',
    },
  },
  required: ['claim', 'evidenceState'],
};

function normalizeEvidenceState(raw: unknown): FindingEvidenceState {
  const s = typeof raw === 'string' ? raw.trim() : '';
  return EVIDENCE_STATES.includes(s as FindingEvidenceState)
    ? (s as FindingEvidenceState)
    : 'unknown';
}

export function normalizeFindingClaim(raw: unknown): FindingClaim | null {
  if (typeof raw === 'string') {
    const claim = raw.trim();
    if (!claim) return null;
    return {
      claim,
      evidenceState: 'unknown',
      openQuestion: claim,
    };
  }
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const claim = typeof o.claim === 'string' ? o.claim.trim() : '';
  if (!claim) return null;
  const evidenceState = normalizeEvidenceState(o.evidenceState);
  let evidence: FindingClaimEvidence | undefined;
  if (o.evidence && typeof o.evidence === 'object' && !Array.isArray(o.evidence)) {
    const ev = o.evidence as Record<string, unknown>;
    const refIds = Array.isArray(ev.refIds)
      ? ev.refIds.filter((n): n is number => typeof n === 'number' && Number.isFinite(n))
      : [];
    const excerpt = typeof ev.excerpt === 'string' ? ev.excerpt.trim() : '';
    if (refIds.length > 0 && excerpt) {
      evidence = { refIds, excerpt };
    }
  }
  const openQuestion = typeof o.openQuestion === 'string' ? o.openQuestion.trim() : undefined;
  const suggestedFollowUp =
    typeof o.suggestedFollowUp === 'string' ? o.suggestedFollowUp.trim() : undefined;
  return {
    claim,
    evidenceState,
    ...(evidence ? { evidence } : {}),
    ...(openQuestion ? { openQuestion } : {}),
    ...(suggestedFollowUp ? { suggestedFollowUp } : {}),
  };
}

export function normalizeFindingClaimArray(raw: unknown): FindingClaim[] {
  if (!Array.isArray(raw)) return [];
  return raw.map(normalizeFindingClaim).filter((c): c is FindingClaim => c !== null);
}

/** Coerce legacy string or structured entry to a FindingClaim (R-131-EPISTEMIC-GROUNDING-C). */
export function coerceFindingClaimEntry(entry: FindingClaimEntry): FindingClaim | null {
  return normalizeFindingClaim(entry);
}

/** R-131-EPISTEMIC-GROUNDING-C — split findings for reader-safe red-flag lists. */
export function partitionFindingClaims(entries: FindingClaimEntry[] | undefined): PartitionedFindingClaims {
  const out: PartitionedFindingClaims = {
    confirmed: [],
    verificationPending: [],
    openQuestions: [],
  };
  for (const raw of entries ?? []) {
    const claim = coerceFindingClaimEntry(raw);
    if (!claim) continue;
    if (claim.evidenceState === 'confirmed') {
      out.confirmed.push(claim);
    } else if (claim.evidenceState === 'likely_present_unverified') {
      out.verificationPending.push(claim);
    } else {
      out.openQuestions.push(claim);
    }
  }
  return out;
}

export function formatOperatorAttestationExcerpt(isoDate: string): string {
  const d = isoDate.slice(0, 10);
  return `Operator confirmed on ${d}`;
}

export function isOperatorAttestedClaim(claim: FindingClaim): boolean {
  const excerpt = claim.evidence?.excerpt?.trim() ?? '';
  return claim.evidenceState === 'confirmed' && excerpt.startsWith('Operator confirmed on ');
}

/** Display text for UI/DOCX/depth-gate — includes open-question suffix when unknown. */
export function formatFindingClaimDisplay(entry: FindingClaimEntry): string {
  if (typeof entry === 'string') return entry.trim();
  const parts = [entry.claim];
  if (entry.evidenceState === 'unknown' && entry.openQuestion?.trim()) {
    parts.push(`(${entry.openQuestion.trim()})`);
  } else if (entry.evidenceState === 'likely_present_unverified') {
    parts.push('(verification pending)');
  }
  return parts.join(' ').trim();
}

export function findingClaimEntriesToStrings(entries: FindingClaimEntry[] | undefined): string[] {
  return (entries ?? []).map(formatFindingClaimDisplay).filter(Boolean);
}

/** Validation for golden fixtures — returns human-readable violations. */
export function validateFindingClaimsEpistemic(claims: FindingClaim[]): string[] {
  const errors: string[] = [];
  for (let i = 0; i < claims.length; i++) {
    const c = claims[i];
    if (c.evidenceState === 'unknown') {
      if (!c.openQuestion?.trim()) {
        errors.push(`[${i}]: evidenceState unknown requires openQuestion`);
      }
      for (const pat of FORBIDDEN_ASSERTED_ABSENCE_PATTERNS) {
        if (pat.test(c.claim)) {
          errors.push(`[${i}]: forbidden asserted-absence phrasing in unknown claim`);
          break;
        }
      }
    } else if (!c.evidence?.refIds?.length || !c.evidence.excerpt?.trim()) {
      errors.push(`[${i}]: evidenceState ${c.evidenceState} requires evidence.refIds and excerpt`);
    } else if (c.evidenceState === 'confirmed') {
      for (const pat of FORBIDDEN_ASSERTED_ABSENCE_PATTERNS) {
        if (pat.test(c.claim) && !/\[Ref\s+\d+\]/i.test(c.claim)) {
          errors.push(`[${i}]: confirmed absence claim lacks [Ref N] citation`);
          break;
        }
      }
    }
  }
  return errors;
}

export function containsForbiddenAssertedAbsence(text: string): boolean {
  return FORBIDDEN_ASSERTED_ABSENCE_PATTERNS.some((p) => p.test(text));
}
